import React, { useState, useEffect } from 'react';
import { agentStatsAPI } from '../../services/api';
import { Clock, User, Phone, TrendingUp, Calendar, Eye, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const AgentStats = () => {
  const [agentStats, setAgentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('average_idle_time_seconds');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchAgentStats();
  }, [dateStart, dateEnd]);

  const fetchAgentStats = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateStart) params.date_start = dateStart;
      if (dateEnd) params.date_end = dateEnd;

      const response = await agentStatsAPI.getIdleTime(params);
      setAgentStats(response.data.data || []);
    } catch (err) {
      setError('Erreur lors du chargement des statistiques');
      console.error('Error fetching agent stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentDetails = async (agentId) => {
    try {
      setDetailsLoading(true);
      const params = {};
      if (dateStart) params.date_start = dateStart;
      if (dateEnd) params.date_end = dateEnd;

      const response = await agentStatsAPI.getAgentDetails(agentId, params);
      setAgentDetails(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des détails de l\'agent');
      console.error('Error fetching agent details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  const sortedStats = [...agentStats].sort((a, b) => {
    const aValue = a[sortBy] || 0;
    const bValue = b[sortBy] || 0;
    
    if (sortOrder === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0s';
    
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const handleViewDetails = (agent) => {
    setSelectedAgent(agent);
    fetchAgentDetails(agent.agent_id);
  };

  const closeModal = () => {
    setSelectedAgent(null);
    setAgentDetails(null);
  };

  if (loading && agentStats.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Statistiques des Agents</h1>
      </div>

      {/* Filtres de date */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtres</h2>
        <form className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de début
              </label>
              <input
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de fin
              </label>
              <input
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => {
                  setDateStart('');
                  setDateEnd('');
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition duration-200"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </form>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <User className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Agents</p>
              <p className="text-2xl font-bold text-gray-900">{agentStats.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Temps Moyen d'Inactivité</p>
              <p className="text-2xl font-bold text-gray-900">
                {agentStats.length > 0 
                  ? formatDuration(agentStats.reduce((sum, agent) => sum + agent.average_idle_time_seconds, 0) / agentStats.length)
                  : '0s'
                }
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <Phone className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Appels</p>
              <p className="text-2xl font-bold text-gray-900">
                {agentStats.reduce((sum, agent) => sum + agent.total_calls, 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Effort Hiérarchique Moyen</p>
              <p className="text-2xl font-bold text-gray-900">
                {agentStats.length > 0 && agentStats.filter(a => a.average_hierarchy_effort_percentage).length > 0
                  ? Math.round(agentStats.filter(a => a.average_hierarchy_effort_percentage).reduce((sum, agent) => sum + (agent.average_hierarchy_effort_percentage || 0), 0) / agentStats.filter(a => a.average_hierarchy_effort_percentage).length) + '%'
                  : 'N/A'
                }
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Temps Total d'Inactivité</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatDuration(agentStats.reduce((sum, agent) => sum + agent.total_idle_time_seconds, 0))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des agents */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
                <th 
                  onClick={() => handleSort('total_calls')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Appels</span>
                    {getSortIcon('total_calls')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('average_idle_time_seconds')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Temps Moyen d'Inactivité</span>
                    {getSortIcon('average_idle_time_seconds')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('total_idle_time_seconds')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Temps Total d'Inactivité</span>
                    {getSortIcon('total_idle_time_seconds')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('average_call_duration_seconds')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Durée Moyenne d'Appel</span>
                    {getSortIcon('average_call_duration_seconds')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('average_hierarchy_effort_percentage')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <span>Effort Hiérarchique Moyen</span>
                    {getSortIcon('average_hierarchy_effort_percentage')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedStats.map((agent) => (
                <tr key={agent.agent_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {agent.agent_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {agent.agent_email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.total_calls}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      agent.average_idle_time_seconds > 300 ? 'bg-red-100 text-red-800' :
                      agent.average_idle_time_seconds > 120 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {formatDuration(agent.average_idle_time_seconds)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDuration(agent.total_idle_time_seconds)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDuration(agent.average_call_duration_seconds)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.average_hierarchy_effort_percentage ? (
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                        agent.average_hierarchy_effort_percentage >= 80 ? 'bg-green-100 text-green-800' :
                        agent.average_hierarchy_effort_percentage >= 60 ? 'bg-blue-100 text-blue-800' :
                        agent.average_hierarchy_effort_percentage >= 40 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {Math.round(agent.average_hierarchy_effort_percentage)}%
                      </span>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                    {agent.total_calls_with_effort > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        {agent.total_calls_with_effort} appel{agent.total_calls_with_effort > 1 ? 's' : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewDetails(agent)}
                      className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Détails</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal des détails */}
      {selectedAgent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails - {selectedAgent.agent_name}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Fermer</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {detailsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : agentDetails ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-600">Total Appels</p>
                      <p className="text-xl font-bold text-gray-900">{agentDetails.total_calls}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-600">Intervalles d'Inactivité</p>
                      <p className="text-xl font-bold text-gray-900">{agentDetails.total_intervals}</p>
                    </div>
                  </div>

                  <h4 className="text-md font-medium text-gray-900 mb-2">Intervalles Détaillés</h4>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Fin Appel Précédent
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Début Appel Suivant
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Temps d'Inactivité
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {agentDetails.intervals?.map((interval, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {formatDate(interval.previous_end_time)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {formatDate(interval.current_start_time)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                interval.idle_time_seconds > 300 ? 'bg-red-100 text-red-800' :
                                interval.idle_time_seconds > 120 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {interval.idle_time_formatted}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Aucun détail disponible</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentStats;
